import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QrCode, RefreshCw, LogOut, Send, Power, RotateCcw, Info, Stethoscope } from "lucide-react";

export default function WhatsApp() {
  const [state, setState] = useState("");
  const [qr, setQr] = useState(null);
  const [qrError, setQrError] = useState("");
  const [loadingQr, setLoadingQr] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testMsg, setTestMsg] = useState("Teste do CobraZap ✅");
  const pollRef = useRef(null);

  const loadState = async () => {
    try {
      const { data } = await api.get("/whatsapp/instance/state");
      setState(data?.state || "");
    } catch (ex) {
      // Silent - connection state polling
      setState("");
    }
  };

  const loadQr = async () => {
    setLoadingQr(true);
    setQrError("");
    try {
      const { data } = await api.get("/whatsapp/instance/qrcode");
      if (data?.state === "open") {
        setState("open");
        setQr(null);
        toast.success("WhatsApp já está conectado!");
      } else if (data?.base64) {
        setQr({ base64: data.base64, code: data.code, pairing_code: data.pairing_code });
      } else {
        setQr(null);
        setQrError(data?.error || "A Evolution não retornou QR. Verifique se a URL/API Key/Instance estão corretos e clique em Criar/Reiniciar Instância.");
      }
      loadState();
    } catch (ex) {
      setQr(null);
      setQrError(formatApiError(ex));
    } finally {
      setLoadingQr(false);
    }
  };

  const createInstance = async () => {
    setLoadingQr(true);
    setQrError("");
    try {
      const { data } = await api.post("/whatsapp/instance/create");
      const q = data?.qr;
      if (q?.base64) {
        setQr({ base64: q.base64, code: q.code, pairing_code: q.pairing_code });
        toast.success("Instância criada. Escaneie o QR.");
      } else if (q?.error) {
        setQrError(q.error);
        toast.error(q.error);
      } else {
        toast.info("Instância criada. Buscando QR...");
        await loadQr();
      }
      loadState();
    } catch (ex) {
      toast.error(formatApiError(ex));
      setQrError(formatApiError(ex));
    } finally {
      setLoadingQr(false);
    }
  };

  const restart = async () => {
    setLoadingQr(true);
    setQrError("");
    try {
      const { data } = await api.post("/whatsapp/instance/restart");
      const q = data?.qr;
      if (q?.base64) {
        setQr({ base64: q.base64, code: q.code, pairing_code: q.pairing_code });
        toast.success("Instância reiniciada. Escaneie o QR.");
      } else {
        await loadQr();
      }
    } catch (ex) {
      toast.error(formatApiError(ex));
      setQrError(formatApiError(ex));
    } finally {
      setLoadingQr(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/whatsapp/instance/logout");
      toast.success("Sessão encerrada");
      setQr(null);
      loadState();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const sendTest = async () => {
    if (!testNumber) return toast.error("Informe o número");
    try {
      await api.post("/whatsapp/send-test", { number: testNumber, message: testMsg });
      toast.success("Mensagem de teste enviada");
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const [diag, setDiag] = useState(null);
  const runDiag = async () => {
    try {
      const { data } = await api.get("/whatsapp/diagnose");
      setDiag(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  useEffect(() => {
    loadState();
    pollRef.current = setInterval(loadState, 6000);
    return () => clearInterval(pollRef.current);
  }, []);

  // If we have a pending QR, auto-refresh every 40s (QR expires ~45-60s)
  useEffect(() => {
    if (!qr) return;
    const t = setTimeout(() => {
      loadState().then(() => {
        if (state !== "open") loadQr();
      });
    }, 40000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr]);

  const connected = state === "open";

  const b64Src = qr?.base64
    ? qr.base64.startsWith("data:")
      ? qr.base64
      : `data:image/png;base64,${qr.base64}`
    : null;

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Evolution API</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">
          Conexão WhatsApp
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Escaneie o QR Code no aparelho conectado ao número que enviará as mensagens. Se o QR não
          aparecer, clique em <span className="text-emerald-300">Criar / Reiniciar Instância</span>.
        </p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Status</div>
          <div className="mt-3 flex items-center gap-3">
            <Badge
              data-testid="whatsapp-status-badge"
              className={
                connected
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : state === "connecting"
                  ? "bg-orange-500/15 text-orange-300 border border-orange-500/30"
                  : "bg-slate-800 text-slate-400 border border-slate-700"
              }
            >
              {connected ? "Conectado" : state === "connecting" ? "Conectando" : "Desconectado"}
            </Badge>
            <button
              onClick={loadState}
              data-testid="refresh-state-btn"
              className="text-xs text-slate-400 hover:text-emerald-400 flex items-center gap-1 transition-colors duration-200"
            >
              <RefreshCw className="w-3 h-3" /> Atualizar
            </button>
          </div>
          <div className="mt-4 space-y-3">
            <Button
              data-testid="create-instance-btn"
              onClick={createInstance}
              disabled={loadingQr}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Power className="w-4 h-4 mr-2" /> Criar Instância
            </Button>
            <Button
              data-testid="load-qr-btn"
              onClick={loadQr}
              disabled={loadingQr}
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 hover:text-white"
            >
              <QrCode className="w-4 h-4 mr-2" /> Gerar / Renovar QR
            </Button>
            <Button
              data-testid="restart-instance-btn"
              onClick={restart}
              disabled={loadingQr}
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reiniciar Instância
            </Button>
            <Button
              data-testid="logout-instance-btn"
              onClick={logout}
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" /> Encerrar Sessão
            </Button>
            <Button
              data-testid="diagnose-btn"
              onClick={runDiag}
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 hover:text-white"
            >
              <Stethoscope className="w-4 h-4 mr-2" /> Diagnóstico
            </Button>
          </div>
          <div className="mt-6 border-t border-slate-800 pt-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Enviar Teste</div>
            <Input
              data-testid="test-number-input"
              placeholder="Número com DDI (ex: 5511999999999)"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
            <Input
              data-testid="test-message-input"
              placeholder="Mensagem"
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
            <Button
              data-testid="send-test-btn"
              onClick={sendTest}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Send className="w-4 h-4 mr-2" /> Enviar
            </Button>
          </div>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">QR Code</div>
            {qr?.pairing_code && (
              <div className="text-xs text-slate-400">
                Código de pareamento: <span className="font-mono-jb text-emerald-300">{qr.pairing_code}</span>
              </div>
            )}
          </div>
          <div className="mt-4 flex items-center justify-center min-h-[380px] rounded-md border border-slate-800 bg-slate-950 p-6">
            {connected ? (
              <div className="text-emerald-300 text-center">
                <div className="text-4xl font-outfit font-bold mb-2">✓</div>
                <div>WhatsApp conectado!</div>
              </div>
            ) : b64Src ? (
              <img
                data-testid="qrcode-image"
                src={b64Src}
                alt="QR Code"
                className="w-72 h-72 rounded-sm bg-white p-2"
              />
            ) : qrError ? (
              <div className="text-center max-w-sm">
                <div className="mx-auto w-10 h-10 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-3">
                  <Info className="w-5 h-5 text-orange-400" />
                </div>
                <div data-testid="qr-error" className="text-sm text-orange-300 leading-relaxed">
                  {qrError}
                </div>
                <div className="text-xs text-slate-500 mt-3">
                  Dica: confira em <span className="text-emerald-300">Configurações</span> se a URL da
                  Evolution, o API Key e o nome da instância estão corretos.
                </div>
              </div>
            ) : (
              <div className="text-slate-500 text-sm text-center max-w-xs">
                Clique em <span className="text-emerald-400">Criar Instância</span> para começar o
                pareamento. Depois, no WhatsApp do celular vá em{" "}
                <span className="text-white">Aparelhos conectados</span> e escaneie o QR.
              </div>
            )}
          </div>
          {qr?.code && (
            <div className="mt-4 text-xs text-slate-500 font-mono-jb break-all">
              <span className="text-slate-600">code:</span> {qr.code}
            </div>
          )}
          {diag && (
            <div className="mt-4 border-t border-slate-800 pt-4 text-xs text-slate-400 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Diagnóstico</div>
              <div>
                URL enviada: <span className="font-mono-jb text-slate-300">{diag.base_url_input}</span>
              </div>
              <div>
                URL normalizada: <span className="font-mono-jb text-emerald-300">{diag.base_url_normalized}</span>
              </div>
              <div>
                Instância: <span className="font-mono-jb text-emerald-300">{diag.instance_name}</span>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500">Ver respostas completas</summary>
                <pre className="mt-2 bg-slate-950 border border-slate-800 rounded p-3 overflow-x-auto text-[10px] text-slate-400">
{JSON.stringify(diag, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
